import { useState, useEffect, useEffectEvent, Activity } from "react";
import { FileText, DollarSign, Package, Calendar, Ban, Check, AlertCircle, CreditCard, Download, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/frontend/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/frontend/components/ui/table";
import { Button } from "@/frontend/components/ui/button";
import { Card, CardContent } from "@/frontend/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/frontend/components/ui/tabs";
import { Tooltip } from "@/frontend/components/ui/tooltip";
import { useToast } from "@/frontend/components/ui/toaster";
import type { SalespersonCommission } from "@/shared/types";
import { formatCurrency } from "@/frontend/lib/utils";
import { exportSalespersonCommissionExcel } from "@/frontend/lib/export-excel";
import { useAuthenticatedApi } from "@x-veloper/dataslot-auth";
import { formatThaiDate } from "@/shared/lib/rfm-translations";
import { t } from "@/shared/lib/th-translations";

interface CreditNoteWithDetails {
  number: string;
  date: string;
  invoiceNumber: string | null;
  totalAmount: number;  // VAT-inclusive amount
  preTaxAmount: number;
  commissionImpact: number;
  invoiceOwner?: {
    salespersonId: string;
    salespersonName: string;
  };
  isLateArrival: boolean;
  maxCommissionRate?: number;
}

interface CreditNotesData {
  creditNotes: CreditNoteWithDetails[];
  summary: {
    totalCount: number;
    totalAmount: number;
    totalPreTaxAmount: number;
    totalCommissionImpact: number;
    periodMatchedCount: number;
    lateArrivalCount: number;
  };
}

interface CreditNotesResponse {
  success: boolean;
  data?: CreditNotesData;
  error?: string;
}

interface InvoiceCommissionDetail {
  taskId: string;
  taskNumber: string;
  customerName: string;
  netAmount: number;
  preTaxAmount: number;
  commissionAmount: number;
  paidAmount: number;
  timestamp: number;
  paidDate: number | null;
  status: string;
  totalPayments?: number;
  hasPaymentsOutsidePeriod?: boolean;
  isExcluded?: boolean;
  exclusionReason?: string;
  creditNotes?: any[];
  creditNoteTotal?: number;
  creditNotePreTaxTotal?: number;
}

interface InvoiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  salespersonId: string | null;
  salespersonName: string | null;
  employeeNumber?: string | null;
  startDate: string | null;
  endDate: string | null;
  isAdmin?: boolean;
  onCommissionRecalculated?: () => void;
}

export function InvoiceDetailsModal({
  isOpen,
  onClose,
  salespersonId,
  salespersonName,
  employeeNumber,
  startDate,
  endDate,
  isAdmin = false,
  onCommissionRecalculated,
}: InvoiceDetailsModalProps) {
  const [commissionData, setCommissionData] = useState<SalespersonCommission | null>(null);
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceCommissionDetail[]>([]);
  const [creditNotesData, setCreditNotesData] = useState<CreditNotesData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [togglingInvoices, setTogglingInvoices] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState<string>('invoices');
  const [isExporting, setIsExporting] = useState(false);
  const { addToast } = useToast();
  const api = useAuthenticatedApi();

  // Admin-only Excel export of the full modal dataset (all 3 tabs).
  const handleExport = async () => {
    if (!isAdmin || !commissionData) return;

    setIsExporting(true);
    try {
      await exportSalespersonCommissionExcel({
        salespersonName: salespersonName || commissionData.salespersonName,
        employeeNumber,
        startDate,
        endDate,
        summary: {
          invoiceCount: commissionData.invoiceCount,
          totalSales: commissionData.totalSales,
          totalCommissionAmount: commissionData.totalCommissionAmount,
        },
        invoices: invoiceDetails.map(inv => ({
          taskNumber: inv.taskNumber,
          customerName: inv.customerName,
          status: inv.status,
          preTaxAmount: inv.preTaxAmount,
          paidAmount: inv.paidAmount,
          paidDate: inv.paidDate,
          commissionAmount: inv.commissionAmount,
          isExcluded: inv.isExcluded,
        })),
        productCommissions: commissionData.productCommissions.map(p => ({
          sku: p.sku,
          category: p.category,
          categoryName: p.categoryName,
          quantity: p.quantity,
          netAmount: p.netAmount,
          commissionRate: p.commissionRate,
          commissionAmount: p.commissionAmount,
        })),
        creditNotes: (creditNotesData?.creditNotes ?? []).map(cn => ({
          number: cn.number,
          date: cn.date,
          invoiceNumber: cn.invoiceNumber,
          totalAmount: cn.totalAmount,
          preTaxAmount: cn.preTaxAmount,
          commissionImpact: cn.commissionImpact,
        })),
      });
    } catch (err) {
      console.error("Error exporting commission details:", err);
      addToast({
        title: t("status.error"),
        description: t("invoiceModal.messages.exportFailed"),
        variant: "destructive",
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Extract non-reactive fetch logic into useEffectEvent
  // This prevents unnecessary re-fetches when props change but modal isn't open
  const onFetchDetails = useEffectEvent(async () => {
    if (!salespersonId || !startDate || !endDate) return;

    // Fetch both invoice and credit notes data in parallel
    await Promise.all([
      fetchInvoiceDetails(),
      fetchCreditNotesDetails()
    ]);
  });

  useEffect(() => {
    if (isOpen) {
      onFetchDetails();
    }
  }, [isOpen]); // Only re-fetch when modal opens

  const fetchInvoiceDetails = async () => {
    if (!salespersonId || !startDate || !endDate) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        period: JSON.stringify({
          year: new Date(endDate).getFullYear(),
          month: new Date(endDate).getMonth() + 1,
        }),
        includeInvoices: 'true',
      });

      const response = await api.request(`/api/salesperson/${salespersonId}?${params}`);
      const data = await response.json() as any;

      if (data.success && data.data?.salesperson) {
        setCommissionData(data.data.salesperson);

        // Process raw tasks to create invoice details
        if (data.data.invoices) {
          console.log('Processing invoices:', data.data.invoices.length);
          console.log('Period:', { startDate, endDate });
          console.log('Sample invoice payments:', data.data.invoices[0]?.detail?.payments);

          const invoices = data.data.invoices.map((task: any) => {
            // Get total paid amount (all payments, not filtered by period)
            const totalPaidAmount = calculateTotalPaidAmount(task);
            const mostRecentPaidDate = getMostRecentPaidDate(task);

            // Also check what's paid in this specific period for reference
            const paidInPeriod = calculatePaidAmountInPeriod(task, new Date(startDate), new Date(endDate));
            const isPaymentInPeriod = paidInPeriod > 0;

            // Log details for invoices with payments
            if (totalPaidAmount > 0) {
              console.log(`Invoice ${task.taskNumber}: Total paid: ${totalPaidAmount}, Most recent date: ${mostRecentPaidDate}, In period: ${paidInPeriod}`);
            }

            return {
              taskId: task.id,
              taskNumber: task.taskNumber,
              customerName: task.detail?.customerInfo?.name || 'N/A',
              netAmount: task.detail.order.netAmount,
              preTaxAmount: task.detail.order.preTaxAmount,
              commissionAmount: calculateTaskCommission(task),
              paidAmount: totalPaidAmount / 1.07,  // Show total paid amount
              timestamp: task.timestamp,
              paidDate: mostRecentPaidDate,  // Show most recent payment date
              status: task.status,
              totalPayments: task.detail?.payments?.filter((p: any) => p.status === 'PAID').length || 0,
              hasPaymentsOutsidePeriod: totalPaidAmount > 0 && !isPaymentInPeriod,
              isExcluded: task.isExcluded || false,
              exclusionReason: task.exclusionReason,
              creditNotes: task.creditNotes || [],
              creditNoteTotal: task.creditNoteTotal || 0,
              creditNotePreTaxTotal: task.creditNotePreTaxTotal || 0,
            };
          });

          const paidInvoices = invoices.filter((inv: any) => inv.paidAmount > 0);
          console.log(`Invoices with payments in period: ${paidInvoices.length} out of ${invoices.length}`);

          setInvoiceDetails(invoices);
        }
      } else {
        setError(data.error || "Failed to fetch commission details");
        setCommissionData(null);
        setInvoiceDetails([]);
      }
    } catch (err) {
      console.error("Error fetching commission details:", err);
      setError("Failed to load commission details");
      setCommissionData(null);
      setInvoiceDetails([]);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to calculate commission for a single task
  const calculateTaskCommission = (task: any): number => {
    let totalCommission = 0;

    if (task.detail?.order?.items) {
      for (const item of task.detail.order.items) {
        // Use commission rate from API if available, otherwise use a default
        const commissionRate = item.commissionRate ?? 0.02;
        // const commissionAmount = item.commissionAmount ?? (item.netAmount * commissionRate);
        const commissionAmount = item.commissionAmount ?? (item.preTaxAmountWithDiscount * commissionRate);

        totalCommission += commissionAmount;
      }
    }

    return totalCommission;
  };

  // Helper function to calculate total paid amount (all payments)
  const calculateTotalPaidAmount = (task: any): number => {
    if (!task.detail?.payments) return 0;

    return task.detail.payments
      .filter((payment: any) => payment.status === 'PAID')
      .reduce((sum: number, payment: any) => sum + payment.amountBaht, 0) / 1.07;
  };

  // Fetch credit notes details from the dedicated API
  const fetchCreditNotesDetails = async () => {
    if (!salespersonId || !endDate) return;

    try {
      const params = new URLSearchParams({
        period: JSON.stringify({
          year: new Date(endDate).getFullYear(),
          month: new Date(endDate).getMonth() + 1,
        }),
      });

      const response = await api.request(`/api/credit-notes/salesperson/${salespersonId}?${params}`);
      const result: CreditNotesResponse = await response.json();

      if (result.success && result.data) {
        setCreditNotesData(result.data);
        console.log(`Fetched ${result.data.creditNotes.length} credit notes for salesperson`);
      } else {
        console.error('Failed to fetch credit notes:', result.error || 'Unknown error');
      }
    } catch (error) {
      console.error('Error fetching credit notes:', error);
    }
  };

  // Helper function to get most recent payment date
  const getMostRecentPaidDate = (task: any): number | null => {
    if (!task.detail?.payments) return null;

    const paidPayments = task.detail.payments
      .filter((payment: any) => payment.status === 'PAID')
      .sort((a: any, b: any) => b.paidTimestamp - a.paidTimestamp);

    return paidPayments.length > 0 ? paidPayments[0].paidTimestamp : null;
  };

  // Helper function to calculate paid amount in period
  const toggleInvoiceExclusion = async (taskId: string, taskNumber: string) => {
    if (!isAdmin || !salespersonId || !startDate || !endDate) return;

    setTogglingInvoices(prev => new Set(prev).add(taskId));

    try {
      const period = {
        year: new Date(endDate).getFullYear(),
        month: new Date(endDate).getMonth() + 1,
      };

      const params = new URLSearchParams({
        period: JSON.stringify(period),
      });

      // Note: isAdminML no longer needed - backend checks role from auth context
      const response = await api.request(`/api/commission-excluded-invoices/toggle?${params}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ taskId }),
      });

      const data = await response.json() as {
        success: boolean;
        data?: { action: 'excluded' | 'included'; taskId: string; message: string };
        error?: string;
      };

      if (data.success && data.data) {
        // Update local state
        setInvoiceDetails(prev => prev.map(inv =>
          inv.taskId === taskId
            ? { ...inv, isExcluded: data.data?.action === 'excluded' }
            : inv
        ));

        addToast({
          title: data.data.action === 'excluded' ? 'Invoice Excluded' : 'Invoice Included',
          description: `Invoice ${taskNumber} has been ${data.data.action === 'excluded' ? 'excluded from' : 'included in'} commission calculation`,
        });

        // Trigger commission recalculation
        if (onCommissionRecalculated) {
          onCommissionRecalculated();
        }

        // Refresh the modal data
        await fetchInvoiceDetails();
      } else {
        throw new Error(data.error || '{t("invoiceModal.messages.toggleFailed")}');
      }
    } catch (error) {
      console.error('Error toggling invoice exclusion:', error);
      addToast({
        title: 'Error',
        description: '{t("invoiceModal.messages.toggleFailed")}. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setTogglingInvoices(prev => {
        const next = new Set(prev);
        next.delete(taskId);
        return next;
      });
    }
  };

  const calculatePaidAmountInPeriod = (task: any, startDate: Date, endDate: Date): number => {
    if (!task.detail?.payments) return 0;

    const paidPayments = task.detail.payments.filter((payment: any) => {
      const isPaid = payment.status === 'PAID';
      const inPeriod = payment.paidTimestamp >= startDate.getTime() &&
        payment.paidTimestamp <= endDate.getTime();

      // Debug logging for first few payments
      if (task.detail.payments.indexOf(payment) < 2) {
        console.log(`Payment check for ${task.taskNumber}:`, {
          status: payment.status,
          paidTimestamp: payment.paidTimestamp,
          paidDate: new Date(payment.paidTimestamp).toISOString(),
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString(),
          isPaid,
          inPeriod
        });
      }

      return isPaid && inPeriod;
    });

    return paidPayments.reduce((sum: number, payment: any) => sum + payment.amountBaht, 0) / 1.07;
  };

  // Calculate filtered stats for admin
  const filteredStats = isAdmin && invoiceDetails.length > 0 ? {
    count: invoiceDetails.filter(inv => inv.isExcluded).length,
    netAmount: invoiceDetails.filter(inv => inv.isExcluded)
      .reduce((sum, inv) => sum + inv.preTaxAmount, 0),
    commission: invoiceDetails.filter(inv => inv.isExcluded)
      .reduce((sum, inv) => sum + inv.commissionAmount, 0)
  } : null;



  return (
    <Dialog open={isOpen} onOpenChange={(open: boolean) => !open && onClose()}>
      <DialogContent className="max-w-7xl max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5" />
            Commission Details: {employeeNumber || salespersonName}
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Period: {startDate && endDate && `${formatThaiDate(startDate)} - ${formatThaiDate(endDate)}`}
          </p>
        </DialogHeader>

        {/* Stats Cards */}
        {commissionData && (
          <>
            <div className={`grid gap-4 mb-4 ${isAdmin ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-2 md:grid-cols-3'}`}>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-muted-foreground">Total Invoices</p>
                      {isAdmin && commissionData.totalInvoiceCount !== undefined && commissionData.totalInvoiceCount !== commissionData.invoiceCount
                        ? (
                          <div className="min-w-0">
                            <p className="text-lg font-bold text-orange-500 truncate">{commissionData.invoiceCount}</p>
                            <p className="text-sm text-green-600 truncate">{commissionData.totalInvoiceCount}</p>
                          </div>
                        )
                        : <p className="text-2xl font-bold">{commissionData.invoiceCount}</p>
                      }
                    </div>
                    <FileText className="h-8 w-8 shrink-0 text-blue-600" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-muted-foreground">Total Sales</p>
                      {isAdmin && commissionData.totalSalesBeforeExclusion !== undefined && commissionData.totalSalesBeforeExclusion !== commissionData.totalSales
                        ? (
                          <div className="min-w-0">
                            <p className="text-lg font-bold text-orange-500 truncate">{formatCurrency(commissionData.totalSales)}</p>
                            <p className="text-sm text-green-600 truncate">{formatCurrency(commissionData.totalSalesBeforeExclusion)}</p>
                          </div>
                        )
                        : <p className="text-2xl font-bold">{formatCurrency(commissionData.totalSales)}</p>
                      }
                    </div>
                    <DollarSign className="h-8 w-8 shrink-0 text-green-600" />
                  </div>
                </CardContent>
              </Card>
              {isAdmin && (
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-muted-foreground">Paid This Period</p>
                        {commissionData.paidAmountBeforeExclusion !== undefined && commissionData.paidAmountBeforeExclusion !== commissionData.paidAmountInPeriod
                          ? (
                            <div className="min-w-0">
                              <p className="text-lg font-bold text-orange-500 truncate">{formatCurrency(commissionData.paidAmountInPeriod)}</p>
                              <p className="text-sm text-green-600 truncate">{formatCurrency(commissionData.paidAmountBeforeExclusion)}</p>
                            </div>
                          )
                          : <p className="text-2xl font-bold">{formatCurrency(commissionData.paidAmountInPeriod)}</p>
                        }
                      </div>
                      <Calendar className="h-8 w-8 shrink-0 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>
              )}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-muted-foreground">Total Commission</p>
                      <p className="text-2xl font-bold">{formatCurrency(commissionData.totalCommissionAmount)}</p>
                    </div>
                    <DollarSign className="h-8 w-8 text-purple-600" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Filtered Stats for Admin - Smaller Cards */}
            {isAdmin && filteredStats && filteredStats.count > 0 && (
              <div className="grid grid-cols-3 gap-2 -mt-5 mb-4">
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Filtered Invoices</p>
                        <p className="text-lg font-bold text-red-600">{filteredStats.count}</p>
                      </div>
                      <Ban className="h-5 w-5 text-red-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Filtered Net Amount</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(filteredStats.netAmount)}</p>
                      </div>
                      <DollarSign className="h-5 w-5 text-red-600" />
                    </div>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs font-medium text-muted-foreground">Filtered Commission</p>
                        <p className="text-lg font-bold text-red-600">{formatCurrency(filteredStats.commission)}</p>
                      </div>
                      <DollarSign className="h-5 w-5 text-red-600" />
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </>
        )}

        {/* Loading State */}
        {loading && (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="flex-1 flex items-center justify-center py-8">
            <div className="text-center">
              <p className="text-red-600 font-medium">Error loading commission details</p>
              <p className="text-sm text-muted-foreground mt-1">{error}</p>
              <Button
                onClick={fetchInvoiceDetails}
                variant="outline"
                size="sm"
                className="mt-4"
              >
                Try Again
              </Button>
            </div>
          </div>
        )}

        {/* Tabs for Commission and Invoice Details */}
        {!loading && !error && commissionData && (
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-3 flex-shrink-0">
              <TabsTrigger value="invoices" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Invoice Details
              </TabsTrigger>
              <TabsTrigger value="products" className="flex items-center gap-2">
                <Package className="h-4 w-4" />
                Product Commissions
              </TabsTrigger>
              <TabsTrigger value="credit-notes" className="flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Credit Notes
              </TabsTrigger>
            </TabsList>

            <TabsContent value="products" className="flex-1 overflow-auto mt-4 min-h-0">
              <Activity mode={activeTab === 'products' ? 'visible' : 'hidden'}>
              {(() => {
                // Filter out 0% commission products for non-admin users
                const displayProducts = isAdmin
                  ? commissionData.productCommissions
                  : commissionData.productCommissions.filter(product => product.commissionRate > 0);

                return displayProducts.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No commission data found for this period</p>
                    </div>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>SKU</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead className="text-right">Quantity</TableHead>
                        <TableHead className="text-right">Net Amount</TableHead>
                        {isAdmin && <TableHead className="text-right">Commission Rate</TableHead>}
                        {isAdmin && <TableHead className="text-right">Commission Amount</TableHead>}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {displayProducts.map((product, index) => (
                        <TableRow key={`${product.sku}-${index}`} className="hover:bg-muted/50">
                        <TableCell className="font-mono text-sm font-medium">
                          {product.sku}
                        </TableCell>
                        <TableCell>
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {product.categoryName || product.category}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          {product.quantity.toLocaleString()}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {formatCurrency(product.netAmount)}
                        </TableCell>
                        {isAdmin && (
                          <TableCell className="text-right">
                            {(product.commissionRate * 100).toFixed(1)}%
                          </TableCell>
                        )}
                        {isAdmin && (
                          <TableCell className="text-right font-semibold">
                            <span className="text-green-700">
                              {formatCurrency(product.commissionAmount)}
                            </span>
                          </TableCell>
                        )}
                      </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                );
              })()}
              </Activity>
            </TabsContent>

            <TabsContent value="invoices" className="flex-1 overflow-auto mt-4 min-h-0">
              <Activity mode={activeTab === 'invoices' ? 'visible' : 'hidden'}>
              {(() => {
                // Filter invoices for non-admin users
                const displayInvoices = isAdmin
                  ? invoiceDetails
                  : invoiceDetails.filter(inv => !inv.isExcluded);

                return displayInvoices.length === 0 ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="text-center">
                      <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <p className="text-muted-foreground">No invoice details found for this period</p>
                    </div>
                  </div>
                ) : (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Task Number</TableHead>
                          <TableHead className="w-[280px]">Customer Name</TableHead>
                          <TableHead className="w-[80px]">Status</TableHead>
                          <TableHead className="text-right">Net Amount</TableHead>
                          <TableHead className="text-right">Total Paid</TableHead>
                          <TableHead className="hidden md:table-cell text-right">Payment Date</TableHead>
                          {isAdmin && <TableHead className="text-right">Commission Amount</TableHead>}
                          {isAdmin && <TableHead className="text-center">Action</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {displayInvoices.map((invoice) => (
                          <TableRow key={invoice.taskNumber} className={`hover:bg-muted/50 ${invoice.isExcluded ? 'opacity-60' : ''}`}>
                            <TableCell className="font-mono text-sm font-medium">
                              <div className="flex items-center gap-2">
                                {invoice.taskNumber}
                                {invoice.isExcluded && (
                                  <Tooltip
                                    content={
                                      <div>
                                        <p>Excluded from commission</p>
                                        {invoice.exclusionReason && (
                                          <p className="text-xs mt-1">Reason: {invoice.exclusionReason}</p>
                                        )}
                                      </div>
                                    }
                                    side="top"
                                  >
                                    <Ban className="h-4 w-4 text-red-500" />
                                  </Tooltip>
                                )}
                                {(invoice.creditNoteTotal ?? 0) > 0 && (
                                  <Tooltip
                                    content={
                                      <div>
                                        <p>Has credit notes</p>
                                        <p className="text-xs mt-1">Total: -{formatCurrency(invoice.creditNoteTotal)}</p>
                                      </div>
                                    }
                                    side="top"
                                  >
                                    <CreditCard className="h-4 w-4 text-orange-500" />
                                  </Tooltip>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">
                              {invoice.customerName}
                            </TableCell>
                            <TableCell>
                              <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${invoice.status === 'PAID'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-yellow-100 text-yellow-800'
                                }`}>
                                {invoice.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {formatCurrency(invoice.preTaxAmount)}
                            </TableCell>
                            <TableCell className="text-right font-semibold">
                              {invoice.paidAmount > 0 ? (
                                <span className={invoice.isExcluded ? 'text-gray-400 line-through' : 'text-green-700'}>
                                  {formatCurrency(invoice.paidAmount)}
                                </span>
                              ) : invoice.hasPaymentsOutsidePeriod ? (
                                <span className="text-muted-foreground" title="Has payments outside this period">
                                  - *
                                </span>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            <TableCell className="hidden md:table-cell text-right text-sm">
                              {invoice.paidDate ? (
                                formatThaiDate(invoice.paidDate)
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </TableCell>
                            {isAdmin && (
                              <TableCell className="text-right font-semibold">
                                <span className={invoice.isExcluded ? 'text-gray-400 line-through' : 'text-green-700'}>
                                  {formatCurrency(invoice.isExcluded ? 0 : invoice.commissionAmount)}
                                </span>
                              </TableCell>
                            )}
                            {isAdmin && (
                              <TableCell className="text-center">
                                <Tooltip
                                  content={invoice.isExcluded ? 'Include in commission' : 'Exclude from commission'}
                                  side="top"
                                >
                                  <Button
                                    size="sm"
                                    variant={invoice.isExcluded ? "destructive" : "outline"}
                                    onClick={() => toggleInvoiceExclusion(invoice.taskId, invoice.taskNumber)}
                                    disabled={togglingInvoices.has(invoice.taskId)}
                                  >
                                    {togglingInvoices.has(invoice.taskId) ? (
                                      <div className="animate-spin h-4 w-4 border-2 border-current border-t-transparent rounded-full" />
                                    ) : invoice.isExcluded ? (
                                      <Check className="h-4 w-4" />
                                    ) : (
                                      <Ban className="h-4 w-4" />
                                    )}
                                  </Button>
                                </Tooltip>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    <div className="mt-4 space-y-2">
                      {displayInvoices.some(inv => inv.hasPaymentsOutsidePeriod) && (
                        <div className="text-sm text-muted-foreground">
                          * Payment occurred outside the selected commission period
                        </div>
                      )}
                      {isAdmin && invoiceDetails.some(inv => inv.isExcluded) && (
                        <div className="flex items-center gap-2 text-sm text-orange-600">
                          <AlertCircle className="h-4 w-4" />
                          <span>
                            {invoiceDetails.filter(inv => inv.isExcluded).length} invoice(s) excluded from commission calculation
                          </span>
                        </div>
                      )}
                    </div>
                  </>
                );
              })()}
              </Activity>
            </TabsContent>

            <TabsContent value="credit-notes" className="flex-1 overflow-auto mt-4 min-h-0">
              <Activity mode={activeTab === 'credit-notes' ? 'visible' : 'hidden'}>
              {(() => {
                // Use credit notes from the dedicated API
                if (!creditNotesData || creditNotesData.creditNotes.length === 0) {
                  return (
                    <div className="flex items-center justify-center py-8">
                      <div className="text-center">
                        <CreditCard className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                        <p className="text-muted-foreground">No credit notes found for this period</p>
                      </div>
                    </div>
                  );
                }

                return (
                  <>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Credit Note Number</TableHead>
                          <TableHead>Date</TableHead>
                          <TableHead>Invoice Number</TableHead>
                          <TableHead className="text-right">VAT-Inclusive Amount</TableHead>
                          <TableHead className="text-right">Pre-Tax Amount</TableHead>
                          <TableHead className="text-right">Commission Impact</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {creditNotesData.creditNotes.map((cn: CreditNoteWithDetails, index: number) => (
                          <TableRow key={`${cn.number}-${index}`} className="hover:bg-muted/50">
                            <TableCell className="font-mono text-sm font-medium">
                              {cn.number}
                            </TableCell>
                            <TableCell>
                              {formatThaiDate(cn.date)}
                            </TableCell>
                            <TableCell className="font-medium">
                              {cn.invoiceNumber}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-red-600">
                              -{formatCurrency(cn.totalAmount)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-red-600">
                              -{formatCurrency(cn.preTaxAmount)}
                            </TableCell>
                            <TableCell className="text-right font-semibold text-orange-600">
                              -{formatCurrency(cn.commissionImpact || 0)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>

                    {/* Summary */}
                    <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex justify-between items-center">
                        <div className="space-y-1">
                          <p className="text-sm font-medium text-red-900">Total Credit Notes Impact</p>
                          <p className="text-xs text-red-700">
                            {creditNotesData.summary.totalCount} credit note(s) reducing commission
                          </p>
                          {creditNotesData.summary.lateArrivalCount > 0 && (
                            <p className="text-xs text-red-600">
                              ({creditNotesData.summary.lateArrivalCount} late arrivals)
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-bold text-red-600">
                            -{formatCurrency(creditNotesData.summary.totalCommissionImpact)}
                          </p>
                          <p className="text-sm text-red-600">
                            Commission Deduction
                          </p>
                          <p className="text-xs text-gray-600 mt-1">
                            (฿{formatCurrency(creditNotesData.summary.totalAmount)} VAT-incl)
                          </p>
                        </div>
                      </div>
                    </div>
                  </>
                );
              })()}
              </Activity>
            </TabsContent>
          </Tabs>
        )}

        {/* Footer */}
        <div className="flex justify-end gap-2 pt-4 border-t flex-shrink-0">
          {isAdmin && (
            <Button
              onClick={handleExport}
              variant="default"
              disabled={isExporting || loading || !commissionData}
            >
              {isExporting ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {isExporting ? t("invoiceModal.exporting") : t("invoiceModal.export")}
            </Button>
          )}
          <Button onClick={onClose} variant="outline">
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}